import React, { useMemo } from 'react';
import { useCrossFlow } from '@atlassiansox/cross-flow-support/cross-flow-api-internals';

import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react/conditional-hooks-factory';

import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts/types';
import { useJiraProductDiscovery } from '@atlassian/global-shortcuts/jira-product-discovery';
import type { CustomItemProps } from '@atlassian/navigation-common/types';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { TryJPDItem } from '../apps/TryJPDItem/TryJPDItem.tsx';

const EMPTY_JPD_SHORTCUT_ITEMS_CONFIG = {};

const useJpdShortcutItemsConfigEnabled = (
	hasJpdLicense: boolean,
): Record<string, GlobalShortcutsItem> => {
	const jpdShortcutBase = useJiraProductDiscovery('');
	const crossFlow = useCrossFlow();
	const shouldShowJpdShortcut =
		!hasJpdLicense &&
		crossFlow.isEnabled &&
		expVal('jpd_try_in_confluence_app_shortcuts', 'isEnabled', false);

	return useMemo(() => {
		if (!shouldShowJpdShortcut) {
			return EMPTY_JPD_SHORTCUT_ITEMS_CONFIG;
		}

		const jpdShortcut: GlobalShortcutsItem = {
			...jpdShortcutBase,
			menuItemId: 'jpdShortcut',
			type: 'component',
			isReorderable: true,
			component: (itemProps: CustomItemProps) => <TryJPDItem {...itemProps} />,
		};

		return { jpdShortcut };
	}, [jpdShortcutBase, shouldShowJpdShortcut]);
};

export const useJpdShortcutItemsConfig: (
	hasJpdLicense: boolean,
) => Record<string, GlobalShortcutsItem> = conditionalHooksFactory(
	() => {
		// Warning: JPD shortcut doesn't work in the new nav (SSR hydration issue).
		// If you're a dev cleaning up navx-global-side-nav-confluence — please fix the hydration if you can,
		// or if you can't — return false here and let the JPD Juno team know that their feature needs to be fixed.
		// Slack channel: #jpd-team-juno (https://atlassian.enterprise.slack.com/archives/C05CRATNGDA)
		return fg('jpd_try_in_confluence_app_shortcuts_ks') && !fg('navx-global-side-nav-confluence');
	},
	useJpdShortcutItemsConfigEnabled,
	() => EMPTY_JPD_SHORTCUT_ITEMS_CONFIG,
);
